import { useState, useEffect, useCallback, useRef } from "react";
import { api, apiError } from "../lib/api";
import { fmtDate, fmtINR } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  FileText, UploadCloud, Eye, RefreshCw, Inbox, Library, HelpCircle, Loader2,
  ScanLine, CheckCircle2, AlertTriangle, Copy, IndianRupee, ChevronDown, ChevronRight,
  Link2, Download, FileUp, X, Sparkles, Bot, HeartPulse,
} from "lucide-react";

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.tif,.tiff";
const TABS = [
  { key: "review", label: "Inbox / Review", icon: Inbox },
  { key: "needs_cust_id", label: "Needs Cust ID", icon: HelpCircle },
  { key: "orphan", label: "Orphans", icon: AlertTriangle },
  { key: "library", label: "Library", icon: Library },
  { key: "unassigned", label: "Unassigned", icon: HelpCircle },
];
const COUNT_TABS = ["review", "needs_cust_id", "orphan", "library", "unassigned"];

const STATUS_BADGE = {
  unassigned: "bg-slate-100 text-slate-600",
  review: "bg-blue-100 text-blue-700",
  needs_cust_id: "bg-amber-100 text-amber-800",
  orphan: "bg-rose-100 text-rose-700",
  matched: "bg-emerald-100 text-emerald-700",
  duplicate: "bg-red-100 text-red-700",
  paid: "bg-green-600 text-white",
  credit_note: "bg-purple-100 text-purple-700",
};
const STATUS_LABEL = {
  unassigned: "Not read", review: "Needs check", needs_cust_id: "Needs Cust ID",
  orphan: "Orphan bill", matched: "Matched-OK",
  duplicate: "Duplicate", paid: "Paid", credit_note: "Credit note",
};
const CONF_BADGE = { high: "bg-emerald-100 text-emerald-700", medium: "bg-amber-100 text-amber-700", low: "bg-slate-100 text-slate-500" };

// Fields shown in the review editor, in display order.
const FIELDS = [
  ["vendor", "Vendor"], ["vendor_gstin", "Vendor GSTIN"], ["invoice_number", "Invoice no."],
  ["invoice_date", "Invoice date"], ["period_from", "Period from"], ["period_to", "Period to"],
  ["circuit_id", "Circuit ID"], ["parent_circuit_id", "Parent circuit ID"], ["account_id", "Account ID"],
  ["registered_mobile", "Reg. mobile"], ["bandwidth", "Bandwidth"],
  ["taxable", "Taxable ₹"], ["gst", "GST ₹"], ["total", "Total ₹ (current, incl GST)"],
  ["previous_balance", "Previous balance ₹"], ["amount_payable", "Amount payable ₹"],
  ["due_date", "Due date"],
  ["end_customer", "End customer (on bill)"], ["install_address", "Install address"],
];

// Source-tag chips for each field (Local / Learned / AI / Needs-you).
const SRC_CHIP = {
  local: { label: "Local", cls: "bg-slate-100 text-slate-600" },
  learned: { label: "Learned", cls: "bg-violet-100 text-violet-700" },
  ai: { label: "AI", cls: "bg-sky-100 text-sky-700" },
  "needs-you": { label: "Needs you", cls: "bg-amber-100 text-amber-700" },
};

function fmtSize(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

async function viewFile(id, version) {
  try {
    const url = `/vendor-invoices/${id}/file${version ? `?version=${version}` : ""}`;
    const { data } = await api.get(url, { responseType: "blob" });
    const objUrl = URL.createObjectURL(data);
    window.open(objUrl, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(objUrl), 60000);
  } catch (e) {
    toast.error(apiError(e.response?.data?.detail) || "Could not open file");
  }
}

// ---------------- Review row (expandable) ----------------
// Small "ISP ✓ · Mbps ✓ · amount ✓" chips showing which clues placed an ILL bill.
function ClueChips({ clues }) {
  if (!clues) return null;
  const labels = { isp: "ISP", mbps: "Mbps", amount: "₹≈ARC", learned: "learned" };
  const on = Object.keys(labels).filter((k) => clues[k]);
  if (!on.length) return null;
  return (
    <span className="inline-flex gap-1">
      {on.map((k) => <span key={k} className="text-[9px] px-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">{labels[k]} ✓</span>)}
    </span>
  );
}

function ReviewRow({ row, canManage, onChanged, selected, onToggleSelect }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ex = row.extracted || {};
  const fields = ex.fields || {};
  const prov = ex.provenance || {};
  const m = row.match || {};
  const checks = row.checks || [];
  const isDup = row.status === "duplicate";

  const [edit, setEdit] = useState(null); // local field edits
  const fieldVal = (k) => (edit && k in edit ? edit[k] : (fields[k] ?? ""));

  const act = async (fn) => { setBusy(true); try { await fn(); await onChanged(); } catch (e) {
    const d = e.response?.data?.detail;
    if (d?.needs_overwrite_confirmation) {
      const ok = window.confirm(`${d.message}\n\n${(d.conflicts || []).map(c => `${c.field}: "${c.existing}" → "${c.from_bill}"`).join("\n")}`);
      if (ok) return act(() => confirmMatch(m.item_id, true));
    } else toast.error(apiError(d) || "Action failed");
  } finally { setBusy(false); } };

  const read = () => act(async () => { await api.post(`/vendor-invoices/${row.id}/read`); toast.success("Auto-read done"); });
  const aiRead = () => act(async () => {
    const { data } = await api.post(`/vendor-invoices/${row.id}/ai-read`);
    toast.success(`AI read done · ₹${data.cost_inr} (₹${data.cap?.remaining_inr} left this month)`);
  });
  const sugg = (outcome) => {
    let body = { outcome };
    if (outcome === "reject") { const r = window.prompt("Why reject this suggestion? (recorded, teaches the AI)"); if (!r) return; body.reason = r; }
    if (outcome === "edit") { const t = window.prompt("Edit the suggestion:", row.suggestion || ""); if (t == null) return; body.edited_text = t; }
    return act(async () => { await api.post(`/vendor-invoices/${row.id}/suggestion`, body); toast.success(`Suggestion ${outcome}ed`); });
  };
  const answer = (qid, value) => act(async () => {
    const { data } = await api.post(`/vendor-invoices/${row.id}/answer`, { question_id: qid, value });
    toast.success(data.applied?.matched ? "Linked — matched" : "Answer saved & learned");
  });
  const confirmMatch = (billableId, overwrite = false) =>
    api.post(`/vendor-invoices/${row.id}/confirm`, { billable_id: billableId, overwrite_ids: overwrite, learn_name: fields.end_customer || null })
      .then((r) => toast.success(`Matched · learned: ${(r.data.learned || []).join(", ") || "—"}`));
  const confirm = (billableId) => act(() => confirmMatch(billableId));
  const saveFields = () => act(async () => {
    await api.post(`/vendor-invoices/${row.id}/fields`, { fields: edit });
    setEdit(null); toast.success("Fields saved");
  });
  const override = () => {
    const reason = window.prompt("Reason for overriding this duplicate (recorded):");
    if (!reason) return;
    act(() => api.post(`/vendor-invoices/${row.id}/duplicate-override`, { reason, as_credit_note: false }));
  };

  // manual assign picker
  const [assignQ, setAssignQ] = useState("");
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/vendor-invoices/billable-options?q=${encodeURIComponent(assignQ)}`); setOpts(data); } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(t);
  }, [assignQ, open]);

  return (
    <>
      <tr className={`hover:bg-slate-50 ${isDup ? "bg-red-50/40" : ""}`}>
        <td className="px-3 py-3">
          {canManage && <input type="checkbox" checked={selected} onChange={() => onToggleSelect(row.id)} />}
        </td>
        <td className="px-2 py-3">
          <button onClick={() => setOpen(!open)} className="text-slate-400 hover:text-slate-700">
            {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
        </td>
        <td className="px-3 py-3">
          <div className="flex items-center gap-2 font-medium text-slate-800">
            <FileText size={15} className="text-slate-400 shrink-0" />
            <span className="truncate max-w-[220px]" title={row.original_filename}>{row.original_filename}</span>
          </div>
          <div className="text-xs text-slate-400 flex items-center gap-1">
            {fields.vendor || "—"} · {fields.invoice_number || "no inv #"}
            {row.bill_kind === "broadband" && <span className="px-1 rounded bg-sky-100 text-sky-700 text-[9px]">Broadband</span>}
            {row.bill_kind === "ill" && <span className="px-1 rounded bg-violet-100 text-violet-700 text-[9px]">ILL</span>}
          </div>
        </td>
        <td className="px-3 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_BADGE[row.status] || "bg-slate-100"}`}>{STATUS_LABEL[row.status] || row.status}</span></td>
        <td className="px-3 py-3 text-sm">
          {m.matched ? (
            <div className="flex items-center gap-1 flex-wrap">
              <Link2 size={13} className="text-emerald-600" />
              <span className="text-slate-700">{m.customer_name || m.internal_billable_id}</span>
              <span className={`text-[10px] px-1.5 rounded-full ${CONF_BADGE[m.confidence] || ""}`}>{m.tier_label}</span>
              {m.resolution === "ill_one" && <ClueChips clues={m.clues} />}
            </div>
          ) : row.status === "needs_cust_id" ? (
            <span className="text-amber-700 text-xs">{m.suggestions?.length || "several"} links fit — pick Cust ID</span>
          ) : row.status === "orphan" ? (
            <span className="text-rose-600 text-xs">couldn't place</span>
          ) : (m.suggestions?.length ? <span className="text-amber-600 text-xs">{m.suggestions.length} suggestion(s)</span>
            : <span className="text-slate-400 text-xs">unmatched</span>)}
        </td>
        <td className="px-3 py-3 text-right font-mono-num">{fmtINR(fields.total)}</td>
        <td className="px-3 py-3">
          {checks.length > 0 && <span className="inline-flex items-center gap-1 text-amber-600 text-xs" title={checks.map(c => c.message).join("\n")}><AlertTriangle size={13} />{checks.length}</span>}
          {isDup && <span className="inline-flex items-center gap-1 text-red-600 text-xs ml-1"><Copy size={13} />dup</span>}
        </td>
        <td className="px-3 py-3">
          <div className="flex items-center justify-end gap-1.5">
            <button onClick={() => viewFile(row.id)} className="px-2 h-8 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100 text-xs inline-flex items-center gap-1"><Eye size={13} />View</button>
            {canManage && !row.read_at && <button disabled={busy} onClick={read} className="px-2 h-8 rounded-lg border border-blue-200 text-blue-700 hover:bg-blue-50 text-xs inline-flex items-center gap-1">{busy ? <Loader2 size={13} className="animate-spin" /> : <ScanLine size={13} />}Read</button>}
            {canManage && row.read_at && !isDup && <button disabled={busy} onClick={aiRead} title="Read this invoice with AI (counts toward the monthly cap)" className="px-2 h-8 rounded-lg border border-sky-200 text-sky-700 hover:bg-sky-50 text-xs inline-flex items-center gap-1"><Sparkles size={13} />Try AI read</button>}
            {canManage && m.matched && !isDup && row.status !== "matched" && row.status !== "paid" &&
              <button disabled={busy} onClick={() => confirm(m.item_id)} className="px-2 h-8 rounded-lg bg-emerald-600 text-white text-xs inline-flex items-center gap-1"><CheckCircle2 size={13} />Confirm</button>}
          </div>
        </td>
      </tr>
      {open && (
        <tr className="bg-slate-50/70">
          <td colSpan={8} className="px-6 py-4">
            {isDup && (
              <div className="mb-3 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700 flex items-center justify-between">
                <span><Copy size={14} className="inline mr-1" /> Duplicate ({row.duplicate_reason}). Original kept — nothing saved.</span>
                {canManage && <button onClick={override} className="px-2.5 h-8 rounded-lg border border-red-300 text-red-700 text-xs">Override (Accounts Mgr)</button>}
              </div>
            )}
            {ex.classification && ex.classification !== "vendor_bill" && (
              <div className="mb-3 rounded-lg bg-amber-50 border border-amber-200 p-2 text-xs text-amber-700">
                Classified as <b>{ex.classification.replace("_", " ")}</b> — {ex.classification === "sales_invoice" ? "a Nextra sales invoice (not a vendor bill)." : ex.classification === "third_party" ? "Nextra is not a party on this bill." : "no text could be read; enter fields manually."}
              </div>
            )}
            {/* extracted fields grid (manual highlighted) */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 text-sm">
              {FIELDS.map(([k, label]) => {
                const src = (ex.sources && ex.sources[k]) || (prov[k] === "manual" ? "needs-you" : "local");
                const chip = SRC_CHIP[src] || SRC_CHIP.local;
                const needs = src === "needs-you";
                return (
                  <div key={k}>
                    <div className="text-[11px] text-slate-400 flex items-center gap-1">
                      {label}
                      <span className={`px-1 rounded text-[9px] font-semibold ${chip.cls}`} title={`source: ${src}`}>{chip.label}</span>
                      {ex.ai_confidence && ex.ai_confidence[k] && src === "ai" && (
                        <span className="text-[9px] text-slate-400">{ex.ai_confidence[k]}</span>
                      )}
                    </div>
                    {canManage ? (
                      <input value={fieldVal(k)} onChange={(e) => setEdit({ ...(edit || {}), [k]: e.target.value })}
                        className={`w-full px-2 py-1 rounded border text-sm ${needs ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"}`} />
                    ) : <div className="text-slate-700">{String(fieldVal(k) || "—")}</div>}
                  </div>
                );
              })}
            </div>
            {ex.text_method && <div className="mt-2 text-[11px] text-slate-400">Read via {ex.text_method} · {ex.text_len} chars</div>}
            {checks.length > 0 && (
              <ul className="mt-3 space-y-1">
                {checks.map((c, i) => <li key={i} className="text-xs text-amber-700 flex items-center gap-1"><AlertTriangle size={12} /> {c.message}</li>)}
              </ul>
            )}
            {canManage && edit && <div className="mt-3"><button disabled={busy} onClick={saveFields} className="px-3 h-8 rounded-lg bg-slate-800 text-white text-xs">Save edited fields</button></div>}

            {/* AI questions (ask-when-unsure) — a person always clicks */}
            {(row.ai_questions || []).filter((q) => !q.answer).map((q) => (
              <div key={q.id} className="mt-3 rounded-lg bg-indigo-50 border border-indigo-200 p-3" data-testid={`ai-question-${q.kind}`}>
                <div className="text-[11px] font-semibold text-indigo-800 flex items-center gap-1"><HelpCircle size={12} /> AI needs a decision</div>
                <div className="text-sm text-slate-800 mt-1">{q.prompt}</div>
                {canManage && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {q.options.map((o) => (
                      <button key={o.value} disabled={busy} onClick={() => answer(q.id, o.value)}
                        title={o.reply || ""}
                        className="px-2.5 h-8 rounded-lg border border-indigo-300 text-indigo-700 bg-white hover:bg-indigo-100 text-xs font-medium">
                        {o.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {(row.ai_auto_applied || []).map((a, i) => (
              <div key={i} className="mt-2 text-xs text-slate-500 flex items-center gap-1">
                <Sparkles size={11} className="text-indigo-400" /> {a.kind === "bill_type" ? "Bill type" : a.kind} set to <b>{a.value}</b> — learned from your answer on {fmtDate(a.learned_at)}
              </div>
            ))}
            {row.cycle_reply && (
              <div className="mt-2 text-xs rounded bg-slate-50 border border-slate-200 p-2 text-slate-600">
                <b>Suggested reply to vendor:</b> {row.cycle_reply}
              </div>
            )}

            {/* AI discrepancy suggestion + finance loop */}
            {row.suggestion && (
              <div className="mt-3 rounded-lg bg-sky-50 border border-sky-200 p-3">
                <div className="text-[11px] font-semibold text-sky-800 flex items-center gap-1"><Sparkles size={12} /> AI suggestion</div>
                <div className="text-sm text-slate-700 mt-1">{row.suggestion_final || row.suggestion}</div>
                {row.suggestion_outcome ? (
                  <div className="text-xs text-slate-500 mt-1">Marked <b>{row.suggestion_outcome}</b>.</div>
                ) : canManage && (
                  <div className="flex items-center gap-2 mt-2">
                    <button disabled={busy} onClick={() => sugg("accept")} className="px-2.5 h-7 rounded-lg bg-emerald-600 text-white text-xs">Accept</button>
                    <button disabled={busy} onClick={() => sugg("reject")} className="px-2.5 h-7 rounded-lg border border-red-300 text-red-700 text-xs">Reject</button>
                    <button disabled={busy} onClick={() => sugg("edit")} className="px-2.5 h-7 rounded-lg border border-slate-300 text-slate-700 text-xs">Edit</button>
                  </div>
                )}
              </div>
            )}

            {/* match / assign */}
            {canManage && !isDup && (
              <div className="mt-4 border-t border-slate-200 pt-3">
                {m.suggestions?.length > 0 && !m.matched && (
                  <div className="mb-2">
                    <div className="text-xs text-slate-500 mb-1">
                      {row.status === "needs_cust_id"
                        ? "These links fit this ISP's bill — confirm the right Cust ID (we never guess):"
                        : "Suggested matches (confirm to link + learn):"}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {m.suggestions.map((s) => (
                        <button key={s.item_id} disabled={busy} onClick={() => confirm(s.item_id)}
                          className="px-2.5 h-8 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-xs">
                          {s.customer_name || s.name} · {s.internal_billable_id}
                          {s.score != null ? ` (${Math.round(s.score * 100)}%)` : (s.bandwidth ? ` · ${s.bandwidth}` : "")}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="text-xs text-slate-500 mb-1">Assign manually:</div>
                <input value={assignQ} onChange={(e) => setAssignQ(e.target.value)} placeholder="search customer / vendor / circuit…"
                  className="w-full md:w-96 px-2 py-1 rounded border border-slate-200 text-sm" />
                {opts.length > 0 && (
                  <div className="mt-1 max-h-40 overflow-auto border border-slate-200 rounded-lg bg-white">
                    {opts.map((o) => (
                      <button key={o.id} disabled={busy} onClick={() => confirm(o.id)}
                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 border-b border-slate-100 last:border-0">
                        <b>{o.customer_name || "—"}</b> · {o.location_name || ""} · {o.vendor_name} · {o.item_type} {o.bandwidth || ""} · {o.internal_billable_id}
                        <span className="text-slate-400"> {o.isp_circuit_id || o.isp_account_number || ""}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// ---------------- Draft import modal ----------------
function DraftImportModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const send = async (path) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post(`/import/invoice-draft/${path}`, fd);
      if (path === "preview") setPreview(data);
      else { toast.success(`Created ${data.links} link(s); skipped ${data.skipped_no_customer} without a confirmed customer`); onDone(); onClose(); }
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(apiError(d) || "Failed");
      if (d?.errors) setPreview({ ok: false, errors: d.errors });
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-head font-bold text-lg flex items-center gap-2"><FileUp size={18} /> Import reviewed draft</h3>
          <button onClick={onClose}><X size={18} className="text-slate-400" /></button>
        </div>
        <p className="text-sm text-slate-500 mb-3">Upload <code>draft_customer_links.xlsx</code> with the <b>Confirmed customer</b> column filled. Rows without a confirmed customer are skipped. Creates customer links so the uploaded invoices can auto-match.</p>
        <input type="file" accept=".xlsx,.csv" onChange={(e) => { setFile(e.target.files[0]); setPreview(null); }} className="text-sm" />
        {preview && (
          <div className="mt-3 rounded-lg border border-slate-200 p-3 text-sm max-h-60 overflow-auto">
            {preview.ok === false ? (
              <div className="text-red-600">{(preview.errors || []).map((e, i) => <div key={i}>Row {e.row}: {e.field} — {e.problem}</div>)}</div>
            ) : (
              <ul className="text-slate-700 space-y-0.5">
                <li>New customers: <b>{preview.summary?.new_customers}</b></li>
                <li>New vendors: <b>{preview.summary?.new_vendors}</b></li>
                <li>New sites: <b>{preview.summary?.new_sites}</b></li>
                <li>Links to create: <b>{preview.summary?.links}</b></li>
                <li>Skipped (no confirmed customer): <b>{preview.summary?.skipped_no_customer}</b></li>
                {(preview.summary?.matched_customers || []).length > 0 && <li className="text-amber-600">Matched existing customers: {preview.summary.matched_customers.map(m => `${m.typed}→${m.matched}`).join(", ")}</li>}
              </ul>
            )}
          </div>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <button disabled={!file || busy} onClick={() => send("preview")} className="px-4 h-10 rounded-lg border border-slate-300 text-sm">{busy ? "…" : "Preview"}</button>
          <button disabled={!file || busy || preview?.ok === false} onClick={() => send("commit")} className="px-4 h-10 rounded-lg bg-[var(--brand)] text-white text-sm font-semibold">Create links</button>
        </div>
      </div>
    </div>
  );
}

// ---------------- Pay modal ----------------
function PayModal({ ids, onClose, onDone }) {
  const [form, setForm] = useState({ payment_date: new Date().toISOString().slice(0, 10), utr: "", remarks: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.utr.trim()) { toast.error("UTR / receipt reference is required"); return; }
    setBusy(true);
    try {
      if (ids.length === 1) await api.post(`/vendor-invoices/${ids[0]}/pay`, form);
      else await api.post(`/vendor-invoices/pay-batch`, { invoice_ids: ids, ...form });
      toast.success(`Marked ${ids.length} invoice(s) paid`); onDone(); onClose();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Payment failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-head font-bold text-lg mb-3 flex items-center gap-2"><IndianRupee size={18} /> Mark {ids.length} invoice(s) paid</h3>
        <p className="text-xs text-slate-500 mb-3">One bank payment to a single vendor. UTR/receipt is mandatory.</p>
        <label className="block text-sm mb-2">Payment date<input type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} className="w-full mt-1 px-2 py-1.5 rounded border border-slate-200" /></label>
        <label className="block text-sm mb-2">UTR / receipt *<input value={form.utr} onChange={(e) => setForm({ ...form, utr: e.target.value })} className="w-full mt-1 px-2 py-1.5 rounded border border-slate-200" /></label>
        <label className="block text-sm mb-3">Remarks<input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="w-full mt-1 px-2 py-1.5 rounded border border-slate-200" /></label>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 h-10 rounded-lg border border-slate-300 text-sm">Cancel</button>
          <button disabled={busy} onClick={submit} className="px-4 h-10 rounded-lg bg-green-600 text-white text-sm font-semibold">{busy ? "…" : "Confirm payment"}</button>
        </div>
      </div>
    </div>
  );
}

export default function Invoices() {
  const { can } = useAuth();
  const canManage = can("manage_invoices");
  const [tab, setTab] = useState("review");
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ review: 0, library: 0, unassigned: 0 });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [reading, setReading] = useState(false);
  const [sel, setSel] = useState(new Set());
  const [showDraft, setShowDraft] = useState(false);
  const [payIds, setPayIds] = useState(null);
  const [facets, setFacets] = useState({ vendors: [], customers: [], statuses: [] });
  const [filters, setFilters] = useState({ vendor: "", customer: "", status: "", month: "" });
  const [aiSummary, setAiSummary] = useState(null);
  const [health, setHealth] = useState(null);
  const [queue, setQueue] = useState(null);
  const fileInput = useRef();
  const replaceInput = useRef();
  const [replaceId, setReplaceId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ queue: tab });
      if (tab === "library") for (const k of ["vendor", "customer", "status", "month"]) if (filters[k]) qs.set(k, filters[k]);
      const [{ data }, counts] = await Promise.all([
        api.get(`/vendor-invoices?${qs}`),
        Promise.all(COUNT_TABS.map((q) => api.get(`/vendor-invoices?queue=${q}&limit=5000`).then((r) => r.data.length))),
      ]);
      setRows(data);
      setCounts(Object.fromEntries(COUNT_TABS.map((q, i) => [q, counts[i]])));
      setSel(new Set());
      api.get("/vendor-invoices/ai/summary").then((r) => setAiSummary(r.data)).catch(() => {});
      api.get("/vendor-invoices/ai/health-report").then((r) => setHealth(r.data)).catch(() => {});
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not load invoices"); }
    finally { setLoading(false); }
  }, [tab, filters]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === "library") api.get("/vendor-invoices/facets").then((r) => setFacets(r.data)).catch(() => {}); }, [tab]);

  const doUpload = async (fileList) => {
    const files = Array.from(fileList || []); if (!files.length) return;
    const fd = new FormData(); files.forEach((f) => fd.append("files", f));
    setUploading(true);
    try {
      const { data } = await api.post("/vendor-invoices/upload", fd);
      const n = data.created?.length || 0;
      if (n) toast.success(`${n} file${n === 1 ? "" : "s"} uploaded — AI reading…`);
      (data.rejected || []).forEach((r) => toast.error(r.reason));
      await load();
      pollQueue();   // show "AI reading…" progress until the queue drains
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.rejected) d.rejected.forEach((r) => toast.error(r.reason)); else toast.error(apiError(d) || "Upload failed");
    } finally { setUploading(false); }
  };

  const readAll = async () => {
    setReading(true);
    try { const { data } = await api.post("/vendor-invoices/read-pending"); toast.success(`Auto-read ${data.read} invoice(s)`); await load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Read failed"); } finally { setReading(false); }
  };

  // Poll the read-queue while uploads process; refresh the list each tick.
  const pollQueue = useCallback(async () => {
    try {
      const { data } = await api.get("/vendor-invoices/ai/queue");
      setQueue(data);
      await load();
      if ((data.queued || 0) + (data.processing || 0) > 0) setTimeout(pollQueue, 3000);
    } catch { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Confirm all: only invoices that are matched, have NO field left "needs-you"
  // or low-confidence, and no blocking checks. Anything else stays for a human.
  const confirmReady = (r) => {
    if (!(r.match?.matched && r.status === "review" && (r.checks || []).length === 0)) return false;
    const src = r.extracted?.sources || {};
    const conf = r.extracted?.ai_confidence || {};
    const keys = ["invoice_number", "invoice_date", "total", "circuit_id"];
    return !keys.some((k) => src[k] === "needs-you" || conf[k] === "low");
  };
  const saveAllOk = async () => {
    const ready = rows.filter(confirmReady);
    if (!ready.length) { toast.info("Nothing is fully clear to confirm (no Needs-you / low-confidence fields)."); return; }
    let ok = 0;
    for (const r of ready) {
      try { await api.post(`/vendor-invoices/${r.id}/confirm`, { billable_id: r.match.item_id, learn_name: r.extracted?.fields?.end_customer || null }); ok++; }
      catch { /* skip conflicts */ }
    }
    toast.success(`Confirmed ${ok} of ${ready.length}`); await load();
  };

  const doReplace = async (fileList) => {
    const f = (fileList || [])[0]; if (!f || !replaceId) return;
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(`/vendor-invoices/${replaceId}/replace`, fd); toast.success("New version uploaded"); await load(); }
    catch (e) { const d = e.response?.data?.detail; toast.error((d?.rejected && d.rejected[0]?.reason) || apiError(d) || "Replace failed"); }
    finally { setReplaceId(null); }
  };

  const downloadZip = async () => {
    if (!sel.size) return;
    try {
      const { data } = await api.get(`/vendor-invoices/download-zip?ids=${[...sel].join(",")}`, { responseType: "blob" });
      const url = URL.createObjectURL(data); const a = document.createElement("a"); a.href = url; a.download = "invoices.zip"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { toast.error("Could not build zip"); }
  };

  const toggleSel = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <FileText className="text-[var(--brand)]" />
          <div>
            <h1 className="font-head font-extrabold text-2xl">Invoices</h1>
            <p className="text-sm text-slate-500">Upload, auto-read, match to billable items, and pay vendor invoices.</p>
          </div>
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            <button onClick={readAll} disabled={reading} className="px-3 h-10 rounded-lg border border-blue-200 text-blue-700 hover:bg-blue-50 text-sm font-semibold inline-flex items-center gap-1.5">{reading ? <Loader2 size={15} className="animate-spin" /> : <ScanLine size={15} />} Read all pending</button>
            <button onClick={() => setShowDraft(true)} className="px-3 h-10 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-semibold inline-flex items-center gap-1.5"><FileUp size={15} /> Import reviewed draft</button>
          </div>
        )}
      </div>

      {/* AI summary + weekly health cards */}
      {(aiSummary || health?.report) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {aiSummary && (
            <div className="rounded-2xl border border-sky-200 bg-sky-50/40 p-4">
              <div className="flex items-center gap-2 font-head font-bold text-sm text-sky-800"><Bot size={16} /> AI assistant</div>
              {(() => {
                const c = aiSummary.cap || {}; const l = aiSummary.last24h || {}; const qz = aiSummary.queue || {};
                const pct = c.cap_inr ? Math.round((c.spent_inr / c.cap_inr) * 100) : 0;
                const busy = (qz.queued || 0) + (qz.processing || 0);
                return (
                  <div className="mt-2 text-sm text-slate-700 space-y-1">
                    <div>Spend this month: <b>₹{c.spent_inr}</b> of ₹{c.cap_inr} ({pct}%){c.capped ? <span className="ml-1 text-red-600 font-semibold">— cap reached, AI paused</span> : pct >= 80 ? <span className="ml-1 text-amber-600 font-semibold">— past 80%</span> : null}</div>
                    <div>Last 24h: <b>{l.processed || 0}</b> processed, {l.ai_read || 0} via AI, cost <b>₹{l.cost_inr || 0}</b></div>
                    <div className="text-xs text-slate-500">AI share: {aiSummary.ai_share_24h_pct ?? 0}% (24h) · {aiSummary.ai_share_pct ?? 0}% all-time — lower is better (local/learned)</div>
                    {busy > 0 && <div className="text-sky-700 font-medium">AI reading… {qz.processing || 0} now, {qz.queued || 0} queued</div>}
                  </div>
                );
              })()}
            </div>
          )}
          {health?.report && (
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="flex items-center gap-2 font-head font-bold text-sm text-slate-800">
                <HeartPulse size={16} /> Weekly health
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${health.verdict === "all_clear" ? "bg-emerald-100 text-emerald-700" : health.verdict === "action_required" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                  {(health.verdict || "").replace("_", " ") || "—"}
                </span>
              </div>
              <pre className="mt-2 text-xs text-slate-600 whitespace-pre-wrap max-h-48 overflow-auto font-sans">{health.report}</pre>
              <div className="text-[10px] text-slate-400 mt-1">as of {fmtDate(health.generated_at)}</div>
            </div>
          )}
        </div>
      )}

      {canManage ? (
        <div onDragOver={(e) => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); doUpload(e.dataTransfer.files); }}
          className={`rounded-2xl border-2 border-dashed p-6 text-center transition-colors ${dragging ? "border-blue-500 bg-blue-50" : "border-slate-300 bg-white"}`} data-testid="invoice-dropzone">
          {uploading ? <div className="flex items-center justify-center gap-2 text-slate-600"><Loader2 className="animate-spin" size={18} /> Uploading…</div> : (
            <>
              <UploadCloud className="mx-auto text-slate-400" size={26} />
              <div className="mt-2 text-sm text-slate-600">Drag &amp; drop invoices here, or <button onClick={() => fileInput.current?.click()} className="text-[var(--brand)] font-semibold underline">choose files</button></div>
              <div className="mt-1 text-xs text-slate-400">PDF, JPG, PNG, TIFF · up to 15 MB each · multiple at once</div>
            </>
          )}
          <input ref={fileInput} type="file" accept={ACCEPT} multiple className="hidden" onChange={(e) => { doUpload(e.target.files); e.target.value = ""; }} />
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-500">View-only access. Uploading/confirming requires the <span className="font-semibold">Manage invoices</span> permission.</div>
      )}

      {/* Sub-tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        {TABS.map((t) => {
          const Icon = t.icon; const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`invoices-tab-${t.key}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px ${active ? "border-[var(--brand)] text-[var(--brand)]" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
              <Icon size={16} /> {t.label}
              <span className={`ml-1 text-xs px-1.5 py-0.5 rounded-full ${active ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-500"}`}>{counts[t.key]}</span>
            </button>
          );
        })}
      </div>

      {/* Action bar */}
      {canManage && (tab === "review" || sel.size > 0) && (
        <div className="flex items-center gap-2 flex-wrap">
          {tab === "review" && <button onClick={saveAllOk} className="px-3 h-9 rounded-lg bg-emerald-600 text-white text-sm font-semibold inline-flex items-center gap-1.5"><CheckCircle2 size={15} /> Confirm all OK</button>}
          {sel.size > 0 && <>
            <span className="text-sm text-slate-500">{sel.size} selected</span>
            <button onClick={() => setPayIds([...sel])} className="px-3 h-9 rounded-lg bg-green-600 text-white text-sm inline-flex items-center gap-1.5"><IndianRupee size={14} /> Mark paid</button>
            <button onClick={downloadZip} className="px-3 h-9 rounded-lg border border-slate-300 text-sm inline-flex items-center gap-1.5"><Download size={14} /> Download zip</button>
          </>}
        </div>
      )}

      {/* Library filters */}
      {tab === "library" && (
        <div className="flex gap-2 flex-wrap">
          {[["vendor", facets.vendors], ["customer", facets.customers], ["status", facets.statuses]].map(([k, opts]) => (
            <select key={k} value={filters[k]} onChange={(e) => setFilters({ ...filters, [k]: e.target.value })} className="px-2 h-9 rounded-lg border border-slate-200 text-sm">
              <option value="">All {k}s</option>
              {opts.map((o) => <option key={o} value={o}>{STATUS_LABEL[o] || o}</option>)}
            </select>
          ))}
          <input type="month" value={filters.month} onChange={(e) => setFilters({ ...filters, month: e.target.value })} className="px-2 h-9 rounded-lg border border-slate-200 text-sm" />
          {(filters.vendor || filters.customer || filters.status || filters.month) && <button onClick={() => setFilters({ vendor: "", customer: "", status: "", month: "" })} className="px-2 h-9 text-sm text-slate-500">Clear</button>}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="w-8 px-3 py-3"></th><th className="w-6"></th>
              <th className="text-left px-3 py-3 font-semibold">File</th>
              <th className="text-left px-3 py-3 font-semibold">Status</th>
              <th className="text-left px-3 py-3 font-semibold">Match</th>
              <th className="text-right px-3 py-3 font-semibold">Total</th>
              <th className="text-left px-3 py-3 font-semibold">Checks</th>
              <th className="text-right px-3 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400"><Loader2 className="inline animate-spin mr-2" size={16} />Loading…</td></tr>
              : rows.length === 0 ? <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">No invoices here yet.</td></tr>
                : rows.map((r) => <ReviewRow key={r.id} row={r} canManage={canManage} onChanged={load} selected={sel.has(r.id)} onToggleSelect={toggleSel} />)}
          </tbody>
        </table>
      </div>

      <input ref={replaceInput} type="file" accept={ACCEPT} className="hidden" onChange={(e) => { doReplace(e.target.files); e.target.value = ""; }} />
      {showDraft && <DraftImportModal onClose={() => setShowDraft(false)} onDone={load} />}
      {payIds && <PayModal ids={payIds} onClose={() => setPayIds(null)} onDone={load} />}
    </div>
  );
}
